import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Col, Row } from "react-bootstrap";
import { get, isEmpty } from "lodash";
import { toast } from "react-toastify";
import { BsCashStack } from "react-icons/bs";

import ActionCard from "components/ActionCard";
import Alert from "components/Alert";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import ContributionSelector from "components/ContributionSelector";
import SimpleModal from "components/SimpleModal";
import WorkerRecordPicker from "components/WorkerRecordPicker";
import ContributionSource from "./ContributionSource";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  getPaycheckSplitContribution,
  getPaychexWorkerRecord,
  getPaychexWorkerRecords,
} from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";
import {
  addPaycheckSplitContributionSource,
  deletePaycheckSplitContributionSource,
  editPaycheckSplitContributionSource,
  getContributionRecommendations,
} from "actions/contributionActions";
import { contributionConstants, userConstants } from "actions/types";
import {
  getIraAccountIdSelector,
  paycheckSplitContributionSelector,
  paychexWorkerRecordsSelector,
  selectedPaychexWorkerRecordSelector,
} from "store/selectors/user";
import {
  activeInvestmentAccountsSelector,
  payrollContributionAccountIdSelector,
} from "store/selectors/accounts";
import { payrollPeriodsToEnglishMapping } from "utils/timeHelper";
import type { RootState } from "types/state";
import type { PaychexWorkerRecord } from "types/models/paychexWorkerRecord";

interface RecommendedContribution {
  recommended: number;
  other: number[];
  isMaxContributionOption?: boolean;
}

interface ContributionRecommendations {
  daily?: RecommendedContribution;
  perPayPeriod?: RecommendedContribution;
}

const crudActions = [
  contributionConstants.PAYCHECK_SPLIT_CONTRIBUTION,
  contributionConstants.EDIT_PAYCHECK_SPLIT_CONTRIBUTION,
  contributionConstants.DELETE_PAYCHECK_SPLIT_CONTRIBUTION,
];
const errorSelector = createErrorSelector([
  ...crudActions,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  userConstants.PAYCHEX_WORKER_RECORDS,
  userConstants.PAYCHEX_WORKER_RECORD,
  userConstants.GET_PAYCHECK_SPLIT_CONTRIBUTION,
]);
// The per-worker fetch (resolves payrollFrequency) then the recommendations
// fetch both gate the edit step's selector.
const isFetchingRecs = createLoadingSelector([
  userConstants.PAYCHEX_WORKER_RECORD,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
]);
const isSubmitting = createLoadingSelector(crudActions);

const mapStateToProps = (state: RootState) => {
  const { dailyMax, perPayPeriodMax, recommendations } = state.contribution;
  return {
    workerRecords: paychexWorkerRecordsSelector(state),
    selectedWorkerRecord: selectedPaychexWorkerRecordSelector(state),
    paycheckSplitContribution: paycheckSplitContributionSelector(state),
    dailyMax,
    perPayPeriodMax,
    dailyRecommendations: (recommendations as ContributionRecommendations)
      .daily,
    perPayPeriodRecommendations: (
      recommendations as ContributionRecommendations
    ).perPayPeriod,
    accountId: getIraAccountIdSelector(state),
    accounts: activeInvestmentAccountsSelector(state),
    payrollContributionAccountId: payrollContributionAccountIdSelector(state),
    error: errorSelector(state),
    isFetchingRecs: isFetchingRecs(state),
    isSubmitting: isSubmitting(state),
  };
};

const mapDispatchToProps = {
  getPaychexWorkerRecords,
  getPaychexWorkerRecord,
  getPaycheckSplitContribution,
  getAllAccounts,
  getContributionRecommendations,
  addPaycheckSplitContributionSource,
  editPaycheckSplitContributionSource,
  deletePaycheckSplitContributionSource,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
  editingWorkerRecordId: string | null;
  deletingWorkerRecordId: string | null;
  isSetupFlowOpen: boolean;
  isMutating: boolean;
}

// Dashboard CRUD surface for Paychex Flex (Perks) PAYCHECK_SPLIT contributions.
// With no split set up yet, a single get-started card opens a modal flow:
// pick a worker record (skipped when there is only one), then choose the
// split amount. Once a split exists, its owning worker record (mapped by
// companyId) renders as a row; edit opens a ContributionSelector modal, delete
// confirms then removes. The active split is read via getPaycheckSplitContribution
// (mirrors recurringContribution); after any mutation we re-read it so the
// dashboard reflects the backend.
class PaycheckSplitCard extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      initialFetching: true,
      editingWorkerRecordId: null,
      deletingWorkerRecordId: null,
      isSetupFlowOpen: false,
      isMutating: false,
    };
  }

  async componentDidMount() {
    const client = this.props.client as ApolloClient<object>;
    // The active split (v1: at most one) is read directly via
    // getPaycheckSplitContribution — merged with its schedule, so it already
    // carries the frequency the row displays; no per-worker fetch is needed here.
    await Promise.all([
      this.props.getAllAccounts(client),
      this.props.getPaychexWorkerRecords(client),
      this.props.getPaycheckSplitContribution(client),
    ]);
    this.setState({ initialFetching: false });
  }

  _workerRecord = (id: string | null) =>
    this.props.workerRecords.find((record) => record.id === id);

  // The worker record that owns the active split, mapped by companyId (unique
  // per Paychex user). Undefined when there is no split or no matching record.
  _recordForSplit = () => {
    const split = this.props.paycheckSplitContribution;
    return split
      ? this.props.workerRecords.find(
          (record) => record.companyId === split.companyId
        )
      : undefined;
  };

  // Re-read the split (and worker records) after any mutation so the dashboard
  // reflects the backend.
  _refreshSplit = () => {
    const client = this.props.client as ApolloClient<object>;
    return Promise.all([
      this.props.getPaychexWorkerRecords(client),
      this.props.getPaycheckSplitContribution(client),
    ]);
  };

  // The per-worker full record (with resolved payrollFrequency) for a given id,
  // when it's the one currently held in redux. Returns undefined otherwise so a
  // stale record from a prior selection isn't used while a new fetch is in
  // flight.
  _fetchedRecordFor = (id: string | null) => {
    const record = this.props.selectedWorkerRecord;
    return record && record.id === id ? record : undefined;
  };

  _editingFullRecord = () =>
    this._fetchedRecordFor(this.state.editingWorkerRecordId);

  _openEdit = async (workerRecordId: string) => {
    this.setState({ editingWorkerRecordId: workerRecordId });
    const client = this.props.client as ApolloClient<object>;
    // The list query leaves payrollFrequency null — fetch the full record to
    // resolve payrollFrequency (and companyId) for the schedule.
    const result = await this.props.getPaychexWorkerRecord(
      client,
      workerRecordId
    );
    const fullRecord = get(result, "data") as PaychexWorkerRecord | undefined;
    // Recommendations key off the resolved payroll frequency.
    await this.props.getContributionRecommendations(client, {
      payrollFrequency: fullRecord?.payrollFrequency,
    });
  };

  _closeEdit = () =>
    this.setState({ editingWorkerRecordId: null, isSetupFlowOpen: false });

  // Get-started flow: with a single worker record there is nothing to pick, so
  // jump straight to the contribution step.
  _startSetup = () => {
    this.setState({ isSetupFlowOpen: true });
    if (this.props.workerRecords.length === 1) {
      void this._openEdit(this.props.workerRecords[0].id);
    }
  };

  _submitSplit = async (
    amount: number,
    isDailyCustom: boolean,
    selectedAccountId: string
  ) => {
    const record = this._workerRecord(this.state.editingWorkerRecordId);
    if (!record) {
      return;
    }
    const accountId =
      selectedAccountId ||
      (this.props.payrollContributionAccountId as string | null) ||
      (this.props.accountId as string | null) ||
      "";

    // Edit when a split already exists for the record being edited (mapped by
    // companyId); otherwise this is the initial add.
    const split = this.props.paycheckSplitContribution;
    const isEditingSplit = !!split && split.companyId === record.companyId;

    this.setState({ isMutating: true });
    try {
      const client = this.props.client as ApolloClient<object>;
      if (isEditingSplit) {
        await this.props.editPaycheckSplitContributionSource(client, {
          id: split.id,
          amount,
          isDailyCustom,
          accountId,
        });
      } else {
        await this.props.addPaycheckSplitContributionSource(client, {
          workerRecordId: record.id,
          accountId,
          amount,
          isDailyCustom,
        });
      }
      await this._refreshSplit();
      if (!this.props.error) {
        toast.success("Successfully updated paycheck contribution");
        this._closeEdit();
      }
    } finally {
      this.setState({ isMutating: false });
    }
  };

  _confirmDelete = async () => {
    const split = this.props.paycheckSplitContribution;
    if (!split) {
      this.setState({ deletingWorkerRecordId: null });
      return;
    }
    this.setState({ isMutating: true });
    try {
      await this.props.deletePaycheckSplitContributionSource(
        this.props.client as ApolloClient<object>,
        { id: split.id }
      );
      await this._refreshSplit();
      if (!this.props.error) {
        toast.success("Removed paycheck contribution");
      }
    } finally {
      this.setState({ isMutating: false, deletingWorkerRecordId: null });
    }
  };

  _renderRow = (record: PaychexWorkerRecord) => {
    // The split (read via getPaycheckSplitContribution) carries its schedule
    // frequency directly; fall back to the record's payrollFrequency.
    const split = this.props.paycheckSplitContribution;
    const frequency = split?.frequency ?? record.payrollFrequency;
    const frequencyLabel = frequency
      ? payrollPeriodsToEnglishMapping[frequency] || frequency
      : "";
    // The stub/backend may reference an account id we can't resolve; fall back
    // the same way the edit modal defaults so the bullet stays meaningful.
    const targetAccount =
      split &&
      (this.props.accounts.find((account) => account.id === split.accountId) ||
        this.props.accounts.find(
          (account) => account.id === this.props.payrollContributionAccountId
        ) ||
        this.props.accounts[0]);
    return (
      <Col md={12} key={record.id}>
        <ContributionSource
          isPaycheckSplit={true}
          contributionSource={{
            name: record.employerName,
            id: record.id,
          }}
          contributionInfo={
            split
              ? {
                  amount: split.amount,
                  schedule: frequencyLabel,
                  accountType: targetAccount?.accountType,
                }
              : {}
          }
          onEditPaycheckSplit={() => void this._openEdit(record.id)}
          onRemovePaycheckSplit={() =>
            this.setState({ deletingWorkerRecordId: record.id })
          }
        />
      </Col>
    );
  };

  _renderContributionSelection = (record: PaychexWorkerRecord) => {
    // Prefer the per-worker fetch (resolves payrollFrequency); the active split
    // (from getPaycheckSplitContribution) supplies the current amount/account
    // for the edit case.
    const fullRecord = this._editingFullRecord();
    // Editing when a split exists for the record being edited (mapped by
    // companyId); otherwise this is the initial add.
    const split = this.props.paycheckSplitContribution;
    const editingSplit =
      split && split.companyId === record.companyId ? split : undefined;
    const isFetching =
      this.props.isFetchingRecs ||
      !fullRecord ||
      isEmpty(this.props.perPayPeriodRecommendations);
    if (isFetching) {
      return <IconSpinner centered />;
    }
    return (
      <div className="contribution-selection">
        <ContributionSelector
          // @ts-expect-error redux types this as number | null; passed through
          dailyMax={this.props.dailyMax}
          // @ts-expect-error redux types this as number | null; passed through
          perPayPeriodMax={this.props.perPayPeriodMax}
          dailyRecommendations={this.props.dailyRecommendations}
          perPayPeriodRecommendations={this.props.perPayPeriodRecommendations}
          error={this.props.error}
          onSubmit={this._submitSplit}
          isSubmitting={this.props.isSubmitting || this.state.isMutating}
          currentContributionAmount={editingSplit?.amount}
          schedule={{ frequency: fullRecord.payrollFrequency ?? "" }}
          isPaycheck={true}
          isEditing={!!editingSplit}
          showAccountSelector={true}
          // @ts-expect-error IraAccount.id/accountType are optional (Maybe)
          accounts={this.props.accounts}
          // @ts-expect-error null preserved; selector output passed through
          defaultAccountId={
            editingSplit?.accountId ||
            this.props.payrollContributionAccountId ||
            this.props.accountId
          }
        />
      </div>
    );
  };

  _renderEditModal = () => {
    const record = this._workerRecord(this.state.editingWorkerRecordId);
    if (!record) {
      return null;
    }
    return (
      <SimpleModal
        show
        title={`How much of each ${record.employerName} paycheck should go to your IRA?`}
        onClose={this._closeEdit}
        // ContributionSelector renders its own submit button.
        hideActions
        buttonText=""
      >
        {this._renderContributionSelection(record)}
      </SimpleModal>
    );
  };

  // Two-step get-started modal: worker record picker first (unless a record is
  // already chosen — auto-selected single record or picked here), then the
  // contribution step.
  _renderSetupModal = () => {
    const record = this._workerRecord(this.state.editingWorkerRecordId);
    return (
      <SimpleModal
        show
        title={
          record
            ? `How much of each ${record.employerName} paycheck should go to your IRA?`
            : "Which employer would you like to deduct from?"
        }
        onClose={this._closeEdit}
        // Each step renders its own submit button (Next / ContributionSelector).
        hideActions
        buttonText=""
      >
        {record ? (
          this._renderContributionSelection(record)
        ) : (
          <WorkerRecordPicker
            hideHeading
            workerRecords={this.props.workerRecords}
            onSelect={(selected) => void this._openEdit(selected.id)}
          />
        )}
      </SimpleModal>
    );
  };

  _renderDeleteModal = () => {
    const record = this._workerRecord(this.state.deletingWorkerRecordId);
    if (!record) {
      return null;
    }
    return (
      <SimpleModal
        show
        title="Remove paycheck contribution?"
        onClose={() => this.setState({ deletingWorkerRecordId: null })}
        // Custom footer: SimpleModal's cancel button is coupled to the header
        // close X (showCloseButton drives both), which we keep hidden.
        hideActions
        buttonText=""
      >
        <p>
          This stops the payroll deduction into your Icon IRA from your{" "}
          {record.employerName} paycheck. You can set it up again at any time.
        </p>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="submit-row">
          <Button
            name="cancel"
            color="cancel"
            btnLabel="Keep it"
            onClick={() => this.setState({ deletingWorkerRecordId: null })}
          />
          <Button
            name="submit"
            color="red"
            btnLabel="Remove contribution"
            loading={this.props.isSubmitting || this.state.isMutating}
            onClick={this._confirmDelete}
          />
        </div>
      </SimpleModal>
    );
  };

  _renderStartCard = () => (
    <Row>
      <Col md={6}>
        <ActionCard
          onClick={this._startSetup}
          icon={<BsCashStack size={16} strokeWidth={0.5} color={"#1D99A9"} />}
          title={"Start making contributions to your IRA"}
          description={
            "Automatically set aside a portion of each paycheck for your retirement"
          }
          actionText={"Start Contributing"}
        />
      </Col>
    </Row>
  );

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }
    if (isEmpty(this.props.workerRecords)) {
      return null;
    }
    // Rule: one active paycheck contribution at a time — when a split exists,
    // render only the worker record that owns it (mapped by companyId).
    const activeRecord = this._recordForSplit();
    return (
      <div className="paycheck-split-card">
        <h1 className="section-title">Paycheck contributions</h1>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {activeRecord ? this._renderRow(activeRecord) : this._renderStartCard()}
        {this.state.isSetupFlowOpen
          ? this._renderSetupModal()
          : this.state.editingWorkerRecordId && this._renderEditModal()}
        {this.state.deletingWorkerRecordId && this._renderDeleteModal()}
      </div>
    );
  }
}

export default connector(withApollo<Props>(PaycheckSplitCard));
